import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Quote,
  Undo,
  Redo,
  AlertCircle
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

interface RichTextEditorProps {
  /** Plain `Form.Item` wiring (antd passes `value` / `onChange`). */
  value?: string;
  /** Explicit content when not used inside `Form.Item`. */
  content?: string;
  onChange?: (content: string) => void;
  onRawTextChange?: (rawText: string) => void; // New callback for raw text
  placeholder?: string;
  minCharacters?: number;
  maxCharacters?: number;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  content,
  onChange,
  onRawTextChange,
  placeholder = "Skriv din text här...",
  minCharacters = 250,
  maxCharacters = 1000,
  className,
}) => {
  const html = value ?? content ?? "";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        limit: maxCharacters,
      }),
    ],
    content: html,
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      const nextHtml = editor.getHTML();
      onChange?.(nextHtml);
      onRawTextChange?.(text);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
  });

  // Update editor when controlled value/content changes (e.g. Form or ad switch)
  useEffect(() => {
    if (editor && html !== editor.getHTML()) {
      editor.commands.setContent(html);
    }
  }, [html, editor]);

  if (!editor) {
    return null;
  }

  const characterCount = editor.storage.characterCount.characters();
  const isUnderMin = characterCount < minCharacters;
  const isOverMax = characterCount > maxCharacters;
  const hasError = isUnderMin || isOverMax;

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleBulletList = () => editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () => editor.chain().focus().toggleOrderedList().run();
  const toggleBlockquote = () => editor.chain().focus().toggleBlockquote().run();
  const undo = () => editor.chain().focus().undo().run();
  const redo = () => editor.chain().focus().redo().run();

  return (
    <div className={cn("border rounded-lg", hasError && "border-red-500", className)}>
      {/* Toolbar */}
      <div className="border-b p-2 flex items-center gap-1 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleBold}
          className={cn("h-8 w-8 p-0", editor.isActive('bold') && "bg-muted")}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleItalic}
          className={cn("h-8 w-8 p-0", editor.isActive('italic') && "bg-muted")}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <div className="w-px h-4 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleBulletList}
          className={cn("h-8 w-8 p-0", editor.isActive('bulletList') && "bg-muted")}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleOrderedList}
          className={cn("h-8 w-8 p-0", editor.isActive('orderedList') && "bg-muted")}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleBlockquote}
          className={cn("h-8 w-8 p-0", editor.isActive('blockquote') && "bg-muted")}
        >
          <Quote className="h-4 w-4" />
        </Button>
        <div className="w-px h-4 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={undo}
          disabled={!editor.can().undo()}
          className="h-8 w-8 p-0"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={redo}
          disabled={!editor.can().redo()}
          className="h-8 w-8 p-0"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Character Count and Validation */}
      <div className="border-t p-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {hasError && (
            <div className="flex items-center gap-1 text-red-500">
              <AlertCircle className="h-4 w-4" />
              <span>
                {isUnderMin && `Minst ${minCharacters} tecken krävs`}
                {isOverMax && `Maximalt ${maxCharacters} tecken tillåts`}
              </span>
            </div>
          )}
        </div>
        <div className={cn(
          "text-muted-foreground",
          hasError && "text-red-500"
        )}>
          {characterCount} / {maxCharacters} tecken
          {isUnderMin && ` (${minCharacters - characterCount} tecken kvar till minimum)`}
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;